import React, { useState, useRef, useEffect } from 'react';
import { Message, SupportArticlePreview, SupportConfig, SupportTicketCreateResult } from '@/types';
import { MessageBubble as MessageComponent } from '@/components/Message';
import { TypingIndicator } from '@/components/TypingIndicator';
import { QuickActions } from '@/components/QuickActions';
import { LeadForm } from '@/components/LeadForm';
import { IconoChat } from '@/components/IconoChat';
import { WidgetIcon } from '@/components/WidgetIcon';
import { PrivacyPolicy } from '@/components/PrivacyPolicy';
import { useTranslation } from '@/lib/i18n';

interface ChatWindowProps {
  config: any;
  messages: Message[];
  isTyping: boolean;
  onSendMessage: (content: string) => void;
  onClose: () => void;
  onClearConversation?: () => void;
  tenantId: string;
  conversationId?: string;
  extractedData?: Record<string, any>;
  privacyNoticeAccepted?: boolean;
  onAcceptPrivacyNotice?: () => void;
  locale?: string;
  chatMode?: 'sales' | 'support';
  onChangeChatMode?: (mode: 'sales' | 'support') => void;
  suggestedArticles?: SupportArticlePreview[];
  supportConfig?: SupportConfig;
  onCreateTicket?: (data: { subject: string; description: string; email?: string; name?: string }) => Promise<SupportTicketCreateResult>;
  reservationData?: Record<string, any>;
  onConfirmReservation?: () => Promise<void>;
  onDismissReservation?: () => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  config,
  messages,
  isTyping,
  onSendMessage,
  onClose,
  onClearConversation,
  tenantId,
  conversationId,
  extractedData,
  privacyNoticeAccepted = true,
  onAcceptPrivacyNotice,
  chatMode = 'sales',
  onChangeChatMode,
  suggestedArticles,
  supportConfig,
  onCreateTicket,
  reservationData,
  onConfirmReservation,
  onDismissReservation,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketEmail, setTicketEmail] = useState('');
  const [ticketName, setTicketName] = useState('');
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [ticketResult, setTicketResult] = useState<SupportTicketCreateResult | null>(null);
  const [isConfirmingReservation, setIsConfirmingReservation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const isSupportMode = chatMode === 'support';
  const isBothMode = config?.mode === 'both';

  // Reservation card: shown when the chatbot has collected all required data
  // (date, time, name and a contact channel). Confirming calls the public
  // reservations API, which validates real availability server-side.
  const reservationReady = (() => {
    if (isSupportMode || !reservationData || !onConfirmReservation) return false;
    if (!reservationData.reservation_date || !reservationData.reservation_time) return false;
    const name = extractedData?.name || extractedData?.customer_name;
    const contact = extractedData?.email || extractedData?.phone;
    return !!(name && contact);
  })();

  const handleConfirmReservation = async () => {
    if (!onConfirmReservation || isConfirmingReservation) return;
    setIsConfirmingReservation(true);
    try {
      await onConfirmReservation();
    } finally {
      setIsConfirmingReservation(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, showLeadForm, showClearConfirm, showTicketForm, ticketResult, reservationReady]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleQuickMessageAction = (value: string) => {
    console.log('[r3Converse] QuickAction message:', value);
    onSendMessage(value);
  };

  const handleQuickLinkAction = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleQuickFormAction = (_formType: string) => {
    setShowLeadForm(true);
  };

  const handleLeadFormSubmit = () => {
    setShowLeadForm(false);
    onSendMessage(t('leadForm.successMessage'));
  };

  const handleLeadFormCancel = () => {
    setShowLeadForm(false);
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketSubject.trim() || !ticketDescription.trim() || !onCreateTicket) return;

    setIsSubmittingTicket(true);
    try {
      const result = await onCreateTicket({
        subject: ticketSubject.trim(),
        description: ticketDescription.trim(),
        email: ticketEmail.trim() || undefined,
        name: ticketName.trim() || undefined,
      });
      setTicketResult(result);
      setTicketSubject('');
      setTicketDescription('');
      setTicketEmail('');
      setTicketName('');
    } catch (err) {
      console.error('[r3Converse] Ticket creation failed:', err);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const showQuickActions = messages.length === 0 && config?.quickActions?.length > 0;

  // The lead form is opened organically: the bot ends the conversation with a
  // message containing the [OPEN_LEAD_FORM] marker. The marker text is ALWAYS
  // stripped from the display (even when the suggestion is disabled — the AI
  // may have emitted it from an older session); the inline button only shows
  // when the tenant has the suggestion enabled (showFormSuggestion !== false).
  const renderMessageWithFormButton = (message: Message) => {
    const hasFormMarker =
      !isSupportMode &&
      message.role === 'assistant' &&
      message.content.includes('[OPEN_LEAD_FORM]');
    const showFormButton = hasFormMarker && config?.showFormSuggestion !== false;
    const displayMessage = hasFormMarker
      ? { ...message, content: message.content.replace('[OPEN_LEAD_FORM]', '').trim() }
      : message;
    return (
      <React.Fragment key={message.id}>
        <MessageComponent message={displayMessage} />
        {showFormButton && (
          <div className="r3c-chat-form-suggestion">
            <button
              onClick={() => setShowLeadForm(true)}
              className="r3c-chat-form-suggestion__btn"
            >
              {t('leadForm.openForm')}
            </button>
          </div>
        )}
      </React.Fragment>
    );
  };

  const greeting = isSupportMode && supportConfig?.greeting
    ? supportConfig.greeting
    : config?.greeting || t('chat.greeting');

  const placeholder = isSupportMode && supportConfig?.placeholder
    ? supportConfig.placeholder
    : config?.placeholder || t('chat.placeholder');

  return (
    <div
      className="r3c-chat-window"
      data-theme={config?.theme || 'dark'}
      style={{ '--r3c-primary-color': config?.primaryColor || '#006cd4' } as React.CSSProperties}
    >
      {/* Header */}
      <div className="r3c-chat-header" style={{ backgroundColor: config?.primaryColor || '#006cd4' }}>
        <div className="r3c-chat-header__info">
          {config?.logoUrl ? (
            <img src={config.logoUrl} alt="" className="r3c-chat-header__logo" />
          ) : config?.avatarUrl ? (
            <img src={config.avatarUrl} alt="" className="r3c-chat-header__avatar" />
          ) : config?.iconStyle && config.iconStyle !== 'brand' ? (
            <WidgetIcon iconStyle={config.iconStyle} className="r3c-chat-header__icon" />
          ) : (
            <IconoChat className="r3c-chat-header__icon" />
          )}
          <div>
            <div className="r3c-chat-header__name">
              {isSupportMode
                ? (supportConfig?.botName || t('support.assistant'))
                : (config?.name || config?.tenantName || t('chat.assistant'))}
            </div>
            <div className="r3c-chat-header__status">{t('chat.online')}</div>
          </div>
        </div>
        <div className="r3c-chat-header__actions">
          {onClearConversation && messages.length > 0 && (
            <button
              className="r3c-chat-header__clear"
              onClick={() => setShowClearConfirm(true)}
              aria-label={t('chat.clearConversation')}
              title={t('chat.clearConversation')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
          )}
          <button className="r3c-chat-header__close" onClick={onClose} aria-label={t('chat.close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mode selector when both sales and support are enabled */}
      {isBothMode && onChangeChatMode && (
        <div className="r3c-chat-mode-selector">
          <button
            className={`r3c-chat-mode-selector__btn ${chatMode === 'sales' ? 'active' : ''}`}
            onClick={() => onChangeChatMode('sales')}
          >
            {t('support.salesMode')}
          </button>
          <button
            className={`r3c-chat-mode-selector__btn ${chatMode === 'support' ? 'active' : ''}`}
            onClick={() => onChangeChatMode('support')}
          >
            {t('support.supportMode')}
          </button>
        </div>
      )}

      {/* Privacy notice */}
      {!showLeadForm && !showTicketForm && !showPrivacyPolicy && !privacyNoticeAccepted && (
        <div className="r3c-privacy-notice">
          <p className="r3c-privacy-notice__text">
            {t('chat.privacyNotice').split('{{privacyPolicyLink}}').map((part, index, arr) => (
              <React.Fragment key={index}>
                {part}
                {index < arr.length - 1 && (
                  <button
                    className="r3c-privacy-notice__link"
                    onClick={() => setShowPrivacyPolicy(true)}
                    type="button"
                  >
                    {t('chat.privacyPolicy')}
                  </button>
                )}
              </React.Fragment>
            ))}
          </p>
          <button className="r3c-privacy-notice__btn" onClick={onAcceptPrivacyNotice}>
            {t('chat.privacyAccept')}
          </button>
        </div>
      )}

      {/* Lead Form / Ticket Form / Privacy Policy or Messages */}
      {showPrivacyPolicy ? (
        <PrivacyPolicy onClose={() => setShowPrivacyPolicy(false)} />
      ) : showLeadForm ? (
        <LeadForm
          tenantId={tenantId}
          conversationId={conversationId}
          prefillData={extractedData}
          fields={config?.leadFields}
          onSubmit={handleLeadFormSubmit}
          onCancel={handleLeadFormCancel}
        />
      ) : showTicketForm ? (
        <div className="r3c-ticket-form">
          <div className="r3c-ticket-form__header">
            <h3>{t('support.createTicket')}</h3>
            <button className="r3c-ticket-form__close" onClick={() => setShowTicketForm(false)} aria-label={t('common.close')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {ticketResult ? (
            <div className="r3c-ticket-form__success">
              <p>{t('support.ticketCreated')}</p>
              <p><strong>{ticketResult.reference}</strong></p>
              <p>{t('support.ticketCreatedHint')}</p>
              <button className="r3c-ticket-form__btn" onClick={() => { setTicketResult(null); setShowTicketForm(false); }}>
                {t('common.back')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreateTicket} className="r3c-ticket-form__form">
              <input
                type="text"
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                placeholder={t('support.ticketSubject')}
                className="r3c-ticket-form__input"
                required
              />
              <textarea
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
                placeholder={t('support.ticketDescription')}
                className="r3c-ticket-form__textarea"
                rows={4}
                required
              />
              {supportConfig?.ticketForm?.askEmail !== false && (
                <input
                  type="email"
                  value={ticketEmail}
                  onChange={(e) => setTicketEmail(e.target.value)}
                  placeholder={t('support.ticketEmail')}
                  className="r3c-ticket-form__input"
                />
              )}
              {supportConfig?.ticketForm?.askName !== false && (
                <input
                  type="text"
                  value={ticketName}
                  onChange={(e) => setTicketName(e.target.value)}
                  placeholder={t('support.ticketName')}
                  className="r3c-ticket-form__input"
                />
              )}
              <button type="submit" className="r3c-ticket-form__btn" disabled={isSubmittingTicket}>
                {isSubmittingTicket ? t('common.submitting') : t('support.submitTicket')}
              </button>
            </form>
          )}
        </div>
      ) : (
        <>
          {/* Messages */}
          <div className="r3c-chat-messages">
            {messages.length === 0 && (
              <div className="r3c-chat-welcome">
                <div className="r3c-chat-welcome__text">{greeting}</div>
              </div>
            )}

            {messages.map((message) => renderMessageWithFormButton(message))}

            {isTyping && <TypingIndicator />}

            {/* Suggested KB articles in support mode */}
            {isSupportMode && suggestedArticles && suggestedArticles.length > 0 && !showClearConfirm && (
              <div className="r3c-suggested-articles">
                <p className="r3c-suggested-articles__title">{t('support.suggestedArticles')}</p>
                {suggestedArticles.map((article) => (
                  <button
                    key={article.id}
                    className="r3c-suggested-articles__item"
                    onClick={() => onSendMessage(article.title)}
                  >
                    {article.title}
                  </button>
                ))}
              </div>
            )}

            {showQuickActions && !showClearConfirm && (
              <QuickActions
                actions={config?.quickActions}
                onMessageAction={handleQuickMessageAction}
                onLinkAction={handleQuickLinkAction}
                onFormAction={handleQuickFormAction}
              />
            )}

            {/* Reservation summary card (sales mode, reservations add-on) */}
            {reservationReady && !showClearConfirm && (
              <div className="r3c-reservation-card">
                <p className="r3c-reservation-card__title">{t('reservations.cardTitle')}</p>
                <p className="r3c-reservation-card__details">
                  📅 {reservationData!.reservation_date} · 🕒 {reservationData!.reservation_time}
                  {' · '}👥 {reservationData!.party_size || 1}
                </p>
                <p className="r3c-reservation-card__customer">
                  {extractedData?.name || extractedData?.customer_name}
                  {extractedData?.phone ? ` · ${extractedData.phone}` : extractedData?.email ? ` · ${extractedData.email}` : ''}
                </p>
                {reservationData!.reservation_notes && (
                  <p className="r3c-reservation-card__notes">📝 {reservationData!.reservation_notes}</p>
                )}
                <div className="r3c-reservation-card__actions">
                  <button
                    className="r3c-reservation-card__btn r3c-reservation-card__btn--confirm"
                    onClick={handleConfirmReservation}
                    disabled={isConfirmingReservation}
                  >
                    {isConfirmingReservation ? t('common.submitting') : t('reservations.confirm')}
                  </button>
                  <button
                    className="r3c-reservation-card__btn r3c-reservation-card__btn--dismiss"
                    onClick={onDismissReservation}
                    disabled={isConfirmingReservation}
                  >
                    {t('reservations.dismiss')}
                  </button>
                </div>
              </div>
            )}

            {/* Create ticket button in support mode */}
            {isSupportMode && supportConfig?.showTicketButton !== false && !showClearConfirm && messages.length > 0 && (
              <div className="r3c-chat-ticket-suggestion">
                <p className="r3c-chat-ticket-suggestion__text">{t('support.ticketSuggestion')}</p>
                <button
                  onClick={() => setShowTicketForm(true)}
                  className="r3c-chat-ticket-suggestion__btn"
                >
                  {t('support.openTicketForm')}
                </button>
              </div>
            )}

            {showClearConfirm && (
              <div className="r3c-chat-clear-confirm">
                <p className="r3c-chat-clear-confirm__text">{t('chat.clearConversationConfirm')}</p>
                <div className="r3c-chat-clear-confirm__actions">
                  <button
                    className="r3c-chat-clear-confirm__btn r3c-chat-clear-confirm__btn--confirm"
                    onClick={() => {
                      onClearConversation?.();
                      setShowClearConfirm(false);
                    }}
                  >
                    {t('chat.clearConversationConfirmBtn')}
                  </button>
                  <button
                    className="r3c-chat-clear-confirm__btn r3c-chat-clear-confirm__btn--cancel"
                    onClick={() => setShowClearConfirm(false)}
                  >
                    {t('chat.clearConversationCancel')}
                  </button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form className="r3c-chat-input" onSubmit={handleSubmit}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={privacyNoticeAccepted ? placeholder : t('chat.privacyInputDisabled')}
              className="r3c-chat-input__field"
              disabled={!privacyNoticeAccepted}
            />
            <button
              type="submit"
              className="r3c-chat-input__send"
              aria-label={t('chat.send')}
              disabled={!privacyNoticeAccepted}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </>
      )}

      {/* Powered by */}
      {config?.showPoweredBy !== false && (
        <div className="r3c-chat-powered-by">
          <span>{'Powered by'}</span>
          <a href="https://r3converse.com" target="_blank" rel="noopener" className="r3c-chat-powered-by__logo">
            <svg viewBox="0 0 100 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <text x="0" y="15" fill="currentColor" fontSize="14" fontWeight="600" fontFamily="system-ui, sans-serif">r3Converse</text>
            </svg>
          </a>
        </div>
      )}
    </div>
  );
};
